import { useState } from "react"
import { Box, Button, Stack, TextField, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If, Else } from "lib/@components/switch"
import { alert } from "lib/@dialogs/alert"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import {
    updateFentraConfig,
    testFentraConnection,
    syncFentraState,
} from "routes/qa/fentra/controllers/fentra-mutations"
import { FentraReadonlyUrlField } from "routes/qa/fentra/config/fentra-readonly-url-field"
import Iconify from "minimals-template/components/Iconify"

export function FentraConfigTab() {
    const { target } = useBoundContext()
    const { _id, ssoCallbackUrl, ssoLoginUrl, ssoMetadataUrl, hasBearer } = target ?? {}
    const [callbackUrl, setCallbackUrl] = useState(ssoCallbackUrl ?? "")
    // Bearer is never returned by the API - only hasBearer is known.
    const [bearer, setBearer] = useState("")
    const [testing, setTesting] = useState(false)
    const [copyCert, setCopyCert] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [syncResult, setSyncResult] = useState(null)

    return (
        <Box sx={{ p: 3, maxWidth: 640 }}>
            <Typography variant="h6" gutterBottom>
                Configuration
            </Typography>

            <Stack spacing={3}>
                {/* ── Step 1: FIQ connection (fill in first) ── */}
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        1. FIQ Connection
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Start in FIQ under <strong>Admin {"→"} Identity Provider</strong> and copy these values here,
                        then save.
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="Reply URL (Assertion Consumer Service)"
                            value={callbackUrl}
                            onChange={(e) => setCallbackUrl(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="https://your-app/auth/sso/callback"
                            helperText={
                                'Copy from FIQ "Service Provider Details" -> Reply URL (Assertion Consumer Service)'
                            }
                            inputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
                        />
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                                label={hasBearer ? "Bearer token (leave blank to keep)" : "Bearer token"}
                                value={bearer}
                                onChange={(e) => setBearer(e.target.value)}
                                fullWidth
                                size="small"
                                type="password"
                                autoComplete="new-password"
                                helperText={
                                    hasBearer
                                        ? "Token already configured - paste a new one to rotate"
                                        : "Create in FIQ under Admin -> Identity Provider -> SCIM connection"
                                }
                                inputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleTestConnection}
                                disabled={testing}
                                sx={{ whiteSpace: "nowrap", mt: "4px" }}
                            >
                                <If value={testing}>
                                    Testing…<Else>Test Connection</Else>
                                </If>
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                <Box>
                    <Button variant="contained" onClick={handleSave}>
                        Save
                    </Button>
                </Box>

                {/* Push current state */}
                <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Push current state
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Re-sends all Fentra users and groups to FIQ via SCIM. Use this after re-enabling SCIM or to
                        recover from failed pushes.
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSync}
                        disabled={syncing}
                        startIcon={<Iconify icon="mdi:sync" />}
                    >
                        <If value={syncing}>
                            Pushing…<Else>Push current state to FIQ</Else>
                        </If>
                    </Button>
                    <If value={syncResult !== null}>
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Users: {syncResult?.usersOk ?? 0} ok
                            <If value={(syncResult?.usersFailed ?? 0) > 0}>, {syncResult?.usersFailed} failed</If>
                            {" · "}Groups: {syncResult?.groupsOk ?? 0} ok
                            <If value={(syncResult?.groupsFailed ?? 0) > 0}>, {syncResult?.groupsFailed} failed</If>
                        </Typography>
                        <If value={syncResult?.errors?.length > 0}>
                            <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                                {syncResult?.errors?.join(" · ")}
                            </Typography>
                        </If>
                    </If>
                </Box>

                {/* ── Step 2: Fentra IdP details (copy back into FIQ after saving) ── */}
                <Box sx={{ p: 2, bgcolor: "background.neutral", borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        2. Fentra Identity Provider Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        After saving above, copy these into FIQ under{" "}
                        <strong>
                            Admin {"→"} Identity Provider {"→"} SSO
                        </strong>
                        .
                    </Typography>
                    <Stack spacing={2}>
                        <FentraReadonlyUrlField
                            label="IdP SSO Login URL"
                            value={ssoLoginUrl ?? ""}
                            helperText='Paste into FIQ as "IdP SSO Login URL"'
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCopyCertificate}
                                disabled={copyCert || !ssoMetadataUrl}
                                startIcon={<Iconify icon="mdi:certificate-outline" />}
                            >
                                <If value={copyCert}>
                                    Copying…<Else>Copy certificate</Else>
                                </If>
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                Paste into FIQ as the <em>IdP Certificate (X.509)</em>
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )

    async function handleSave() {
        try {
            await updateFentraConfig({
                id: _id,
                // Only send bearer if the user typed one - blank means "no change"
                bearer: bearer || undefined,
                ssoCallbackUrl: callbackUrl,
            })
            invalidate("fentraProfiles")
            setBearer("")
            successSnackbar("Configuration saved")
        } catch (err) {
            errorSnackbar(err?.message || "Failed to save configuration")
        }
    }

    async function handleSync() {
        setSyncing(true)
        setSyncResult(null)
        try {
            const result = await syncFentraState({ profileId: _id })
            setSyncResult(result)
            if (result.usersFailed === 0 && result.groupsFailed === 0) {
                successSnackbar(`Pushed ${result.usersOk} users and ${result.groupsOk} groups`)
            } else {
                errorSnackbar(`Push completed with errors — check below`)
            }
        } catch (err) {
            errorSnackbar(err?.message || "Sync failed")
        } finally {
            setSyncing(false)
        }
    }

    async function handleCopyCertificate() {
        setCopyCert(true)
        try {
            const response = await fetch(ssoMetadataUrl)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            // A frontend host serves the SPA shell (200 text/html) for unrouted paths, so an
            // ok response is not proof we reached the mock IdP. Check the type before parsing,
            // otherwise the failure surfaces as a confusing "no certificate in metadata".
            const contentType = response.headers.get("content-type") ?? ""
            const xml = await response.text()
            if (!contentType.includes("xml")) {
                throw new Error(
                    `Expected SAML metadata XML from ${ssoMetadataUrl} but received ${contentType || "an unknown type"} — the mock IdP is served by the API, so check the API base URL configuration`
                )
            }
            const match = xml.match(/<[^:>\s]*:?X509Certificate[^>]*>([\s\S]+?)<\/[^:>\s]*:?X509Certificate>/)
            if (!match) throw new Error("No X509Certificate found in metadata")
            const cert = match[1].replace(/\s+/g, "")
            const pem = `-----BEGIN CERTIFICATE-----\n${cert.match(/.{1,64}/g).join("\n")}\n-----END CERTIFICATE-----`
            await navigator.clipboard.writeText(pem)
            successSnackbar("Certificate copied - paste into FIQ as the IdP Certificate (X.509)")
        } catch (err) {
            errorSnackbar(err?.message || "Failed to copy certificate")
        } finally {
            setCopyCert(false)
        }
    }

    async function handleTestConnection() {
        setTesting(true)
        try {
            const result = await testFentraConnection({ profileId: _id })
            if (result?.ok) {
                successSnackbar("Connection test passed")
            } else {
                const msg = result?.scimStatus
                    ? `Connection failed (SCIM ${result.scimStatus}): ${result.error}`
                    : `Connection failed: ${result?.error ?? "Unknown error"}`
                await alert(msg, "Test Connection")
            }
        } catch (err) {
            errorSnackbar(err?.message || "Test connection failed")
        } finally {
            setTesting(false)
        }
    }
}
